import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import { Helmet } from 'react-helmet';

const DEFAULT_IMAGE = 'https://cdn.pixabay.com/photo/2016/08/08/09/17/avatar-1577909_640.png';

const UserList = () => {

  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePlans, setActivePlans] = useState([]);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);
  const [subchildCategories, setSubchildCategories] = useState([]);
  const [displayCount, setDisplayCount] = useState(20);

  const isLoggedIn = localStorage.getItem('user') !== null;
  const userss = isLoggedIn ? JSON.parse(localStorage.getItem('user')) : null;

  const [filters, setFilters] = useState({
    name: '',
    userType: '',
    contact_no: '',
    city: '',
    state: '',
    pincode: '',
    parent_category_id: '',
    child_category_id: '',
    subchild_category_id: '',
    status: ''
  });

  const [sortConfig, setSortConfig] = useState({
    key: 'created_at',
    direction: 'desc'
  });

  const fetchCategories = async () => {
    try {
      const [catRes, childRes, subchildRes] = await Promise.all([
        api.get('/categories'),
        api.get('/child-categories'),
        api.get('/subchild-categories'),
      ]);

      setCategories(catRes.data || []);
      setChildCategories(childRes.data || []);
      setSubchildCategories(subchildRes.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchActivePlans = async () => {
    try {
      const response = await api.get('/user/active-plans');
      setActivePlans(response.data.plans || []);
    } catch (error) {
      console.error('Error fetching active plans:', error);
      setActivePlans([]);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const userTypeFromUrl = searchParams.get("type");

    if (userTypeFromUrl) {
      setFilters(prev => ({
        ...prev,
        userType: userTypeFromUrl
      }));
    }
  }, [location.search]);

  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    const handleKeyDown = (e) => {
      if (e.key === "F12") e.preventDefault();
      if (e.ctrlKey && e.shiftKey && (e.key === "I" || e.key === "J")) e.preventDefault();
      if (e.ctrlKey && (e.key === "U" || e.key === "S")) e.preventDefault();
    };
    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);


  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};

      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params[key] = filters[key];
        }
      });

      if (filters.userType) {
        params.type = filters.userType;
      }

      const response = await api.get('/users', { params });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch users');
      }

      setUsers(Array.isArray(response.data.users) ? response.data.users : []);

    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const hasActivePlan = activePlans.some(plan => plan.user_id === userss?.id);
  const loggedInUserType = userss?.user_type?.toLowerCase();

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleApplyFilters = () => {
    fetchUsers();
  };

  const handleResetFilters = () => {
    setFilters({
      name: '',
      userType: '',
      contact_no: '',
      city: '',
      state: '',
      pincode: '',
      parent_category_id: '',
      child_category_id: '',
      subchild_category_id: '',
      status: ''
    });
    fetchUsers();
  };

  useEffect(() => {
    fetchUsers();
    fetchActivePlans();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortConfig]);

  useEffect(() => {
    const handleScroll = () => {
      const bottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 200;
      if (bottom) {
        setDisplayCount(prev => prev + 20);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const sortedUsers = useMemo(() => {
    let sorted = [...users];

    sorted.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'created_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [users, sortConfig]);

  /* ===================== USER TYPE LABEL HELPER ===================== */
  const getUserTypeLabel = (userType) => {
    if (userType === "Business") return "Business for Sell";
    if (userType === "Employee") return "Skilled Business Employee";
    return userType;
  };

  /* ===================== PROTECTED FIELD HELPER ===================== */
  const renderProtectedValue = (value, viewedUserType, options = {}) => {
    if (!value) return <span className="ul-muted">N/A</span>;

    const viewedType = viewedUserType?.toLowerCase();

    // Case 1: Viewed user is NOT partner → always visible
    if (viewedType !== 'partner') {
      return options.link ? (
        <a
          href={options.link}
          className={options.className}
          onClick={(e) => e.stopPropagation()}
        >
          {options.icon}
          {value}
        </a>
      ) : (
        <span>{value}</span>
      );
    }

    // Case 2: Viewed user IS partner
    // Only partner + active plan can see
    if (loggedInUserType === 'partner' && hasActivePlan) {
      return options.link ? (
        <a
          href={options.link}
          className={options.className}
          onClick={(e) => e.stopPropagation()}
        >
          {options.icon}
          {value}
        </a>
      ) : (
        <span>{value}</span>
      );
    }

    // All other cases → hide / blur
    return (
      <div className="d-flex flex-column align-items-start">
        <span className="ul-blur">{value}</span>
        <small className="ul-blur-note">Partners with an active plan can view</small>
      </div>
    );
  };


  const filteredUsers = useMemo(() => {
    return sortedUsers.filter((user) => {

      if (filters.name && !user.name?.toLowerCase().includes(filters.name.toLowerCase())) {
        return false;
      }

      if (filters.userType && user.user_type?.toLowerCase() !== filters.userType.toLowerCase()) {
        return false;
      }

      if (filters.contact_no && !user.contact_no?.includes(filters.contact_no)) {
        return false;
      }

      if (filters.city && !user.city?.toLowerCase().includes(filters.city.toLowerCase())) {
        return false;
      }

      if (filters.state && !user.state?.toLowerCase().includes(filters.state.toLowerCase())) {
        return false;
      }

      if (filters.pincode && !user.pincode?.toString().includes(filters.pincode)) {
        return false;
      }

      if (filters.parent_category_id && user.parent_category_id?.toString() !== filters.parent_category_id.toString()) {
        return false;
      }

      if (filters.child_category_id && user.child_category_id?.toString() !== filters.child_category_id.toString()) {
        return false;
      }

      if (filters.subchild_category_id && user.subchild_category_id?.toString() !== filters.subchild_category_id.toString()) {
        return false;
      }

      if (filters.status && user.status?.toString() !== filters.status.toString()) {
        return false;
      }

      return true;
    });
  }, [sortedUsers, filters]);

  const handleUserClick = (user) => {
    navigate(`/user-details/${user.user_id}`, { state: { user } });
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <span className="ul-sort-icon ul-sort-idle">⇅</span>;
    }
    return (
      <span className="ul-sort-icon ul-sort-active">
        {sortConfig.direction === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="user-list-page ul-page">
      <Helmet>
        <title>All Users</title>
      </Helmet>

      <Navbar />

      {/* ===================== SCOPED STYLES ===================== */}
      <style>{`
        .ul-page {
          --ul-ink: #16202b;
          --ul-ink-soft: #5b6b7c;
          --ul-line: #e3e8ee;
          --ul-surface: #ffffff;
          --ul-canvas: #f4f6f9;
          --ul-accent: #2b5cb8;
          --ul-accent-soft: #eaf1ff;
          --ul-good: #1c8a5e;
          --ul-good-soft: #e7f7f0;
          --ul-warn: #b8752b;
          --ul-warn-soft: #fdf1e2;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          color: var(--ul-ink);
          background: var(--ul-canvas);
        }

        .ul-shell {
          width: 100%;
          max-width: 1440px;
          margin: 0 auto;
          padding: 28px clamp(12px, 3vw, 36px) 56px;
        }

        .ul-heading {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 20px;
        }

        .ul-heading h1 {
          font-size: 1.7rem;
          font-weight: 700;
          letter-spacing: -0.01em;
          margin: 0;
          color: var(--ul-ink);
        }

        .ul-heading p {
          margin: 2px 0 0;
          color: var(--ul-ink-soft);
          font-size: 0.95rem;
        }

        /* ---------- FILTER PANEL ---------- */
        .ul-filter-card {
          background: var(--ul-surface);
          border: 1px solid var(--ul-line);
          border-radius: 16px;
          box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
          margin-bottom: 28px;
          overflow: hidden;
        }

        .ul-filter-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 28px;
          border-bottom: 1px solid var(--ul-line);
        }

        .ul-filter-header h2 {
          font-size: 1.15rem;
          font-weight: 700;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .ul-filter-count {
          background: var(--ul-accent-soft);
          color: var(--ul-accent);
          font-size: 0.78rem;
          font-weight: 700;
          padding: 3px 10px;
          border-radius: 999px;
        }

        .ul-filter-body {
          padding: 26px 28px 22px;
        }

        .ul-field-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 20px 22px;
        }

        @media (max-width: 1200px) {
          .ul-field-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        @media (max-width: 860px) {
          .ul-field-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 560px) {
          .ul-field-grid { grid-template-columns: 1fr; }
        }

        .ul-field label {
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--ul-ink-soft);
          margin-bottom: 7px;
        }

        .ul-field input,
        .ul-field select {
          width: 100%;
          height: 46px;
          font-size: 0.95rem;
          padding: 0 14px;
          border-radius: 10px;
          border: 1.5px solid var(--ul-line);
          background: var(--ul-canvas);
          color: var(--ul-ink);
          transition: border-color 0.15s ease, background 0.15s ease;
        }

        .ul-field input::placeholder { color: #9aa7b4; }

        .ul-field input:focus,
        .ul-field select:focus {
          outline: none;
          border-color: var(--ul-accent);
          background: var(--ul-surface);
          box-shadow: 0 0 0 3px var(--ul-accent-soft);
        }

        .ul-filter-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px dashed var(--ul-line);
        }

        .ul-btn {
          height: 44px;
          padding: 0 22px;
          border-radius: 10px;
          font-weight: 700;
          font-size: 0.92rem;
          border: none;
          cursor: pointer;
          transition: transform 0.1s ease, opacity 0.15s ease;
        }
        .ul-btn:active { transform: translateY(1px); }

        .ul-btn-primary { background: var(--ul-accent); color: #fff; }
        .ul-btn-primary:hover { opacity: 0.92; }

        .ul-btn-ghost {
          background: transparent;
          color: var(--ul-ink-soft);
          border: 1.5px solid var(--ul-line);
        }
        .ul-btn-ghost:hover { background: var(--ul-canvas); }

        /* ---------- RESULTS CARD ---------- */
        .ul-results-card {
          background: var(--ul-surface);
          border: 1px solid var(--ul-line);
          border-radius: 16px;
          box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
          overflow: hidden;
        }

        .ul-results-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 18px 28px;
          border-bottom: 1px solid var(--ul-line);
          background: var(--ul-surface);
        }

        .ul-results-header h3 {
          margin: 0;
          font-size: 1.05rem;
          font-weight: 700;
        }

        .ul-results-header span {
          color: var(--ul-ink-soft);
          font-weight: 500;
        }

        .ul-table-wrap { overflow-x: auto; width: 100%; }

        .ul-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 920px;
        }

        .ul-table thead th {
          background: var(--ul-canvas);
          color: var(--ul-ink-soft);
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          font-weight: 700;
          text-align: left;
          padding: 14px 18px;
          border-bottom: 1px solid var(--ul-line);
          white-space: nowrap;
        }

        .ul-th-btn {
          background: none;
          border: none;
          padding: 0;
          font: inherit;
          color: inherit;
          display: inline-flex;
          align-items: center;
          cursor: pointer;
        }

        .ul-sort-icon { margin-left: 8px; font-size: 0.85rem; }
        .ul-sort-idle { opacity: 0.35; }
        .ul-sort-active { color: var(--ul-accent); }

        .ul-table tbody tr {
          border-bottom: 1px solid var(--ul-line);
          cursor: pointer;
          transition: background 0.12s ease;
        }
        .ul-table tbody tr:hover { background: var(--ul-canvas); }
        .ul-table tbody tr:last-child { border-bottom: none; }

        .ul-table td {
          padding: 16px 18px;
          font-size: 0.95rem;
          vertical-align: middle;
        }

        .ul-avatar {
          width: 46px;
          height: 46px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid var(--ul-line);
        }

        .ul-name {
          font-size: 1rem;
          font-weight: 700;
          color: var(--ul-ink);
        }

        .ul-badge-inactive {
          margin-left: 8px;
          font-size: 0.72rem;
          font-weight: 700;
          background: var(--ul-warn-soft);
          color: var(--ul-warn);
          padding: 3px 9px;
          border-radius: 999px;
        }

        .ul-type-chip {
          display: inline-flex;
          align-items: center;
          max-width: 200px;
          font-size: 0.82rem;
          font-weight: 600;
          line-height: 1.25;
          padding: 6px 12px;
          border-radius: 999px;
          background: var(--ul-accent-soft);
          color: var(--ul-accent);
          white-space: normal;
        }

        .ul-muted { color: #9aa7b4; }

        .ul-contact-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          height: 36px;
          padding: 0 14px;
          font-size: 0.85rem;
          font-weight: 700;
          border-radius: 8px;
          background: var(--ul-good-soft);
          color: var(--ul-good);
          text-decoration: none;
        }
        .ul-contact-link:hover { color: var(--ul-good); opacity: 0.85; }

        .ul-blur {
          filter: blur(3px);
          user-select: none;
          font-size: 0.92rem;
        }
        .ul-blur-note {
          color: var(--ul-ink-soft);
          font-size: 0.72rem;
          margin-top: 2px;
        }

        .ul-place { font-size: 0.92rem; }

        .ul-view-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          height: 36px;
          padding: 0 16px;
          border-radius: 8px;
          border: none;
          background: var(--ul-accent);
          color: #fff;
          font-weight: 700;
          font-size: 0.85rem;
          cursor: pointer;
        }
        .ul-view-btn:hover { opacity: 0.92; }

        .ul-load-more {
          text-align: center;
          padding: 16px;
          color: var(--ul-ink-soft);
          font-size: 0.9rem;
        }

        .ul-state {
          text-align: center;
          padding: 64px 20px;
        }

        .ul-state-icon { font-size: 2.6rem; color: var(--ul-ink-soft); margin-bottom: 10px; }
      `}</style>

      <section className="ul-shell">

        <div className="ul-heading">
          <div>
            <h1>All Users</h1>
            <p>Search and manage every registered user in one place.</p>
          </div>
        </div>

        {/* ====================== FILTER PANEL ====================== */}
        <div className="ul-filter-card">
          <div className="ul-filter-header">
            <h2>
              Filter Users
              {activeFilterCount > 0 && (
                <span className="ul-filter-count">{activeFilterCount} active</span>
              )}
            </h2>
          </div>

          <div className="ul-filter-body">
            <div className="ul-field-grid">

              {/* NAME */}
              <div className="ul-field">
                <label>Name</label>
                <input
                  type="text"
                  value={filters.name}
                  onChange={(e) => handleFilterChange("name", e.target.value)}
                  placeholder="Search by name"
                />
              </div>

              {/* USER TYPE */}
              <div className="ul-field">
                <label>User Type</label>
                <input
                  type="text"
                  value={filters.userType}
                  onChange={(e) => handleFilterChange("userType", e.target.value)}
                  placeholder="Business, Employee, Partner..."
                />
              </div>

              {/* CONTACT */}
              <div className="ul-field">
                <label>Mobile No</label>
                <input
                  type="text"
                  value={filters.contact_no}
                  onChange={(e) => handleFilterChange("contact_no", e.target.value)}
                  placeholder="Search by phone"
                />
              </div>

              {/* CITY */}
              <div className="ul-field">
                <label>City</label>
                <input
                  type="text"
                  value={filters.city}
                  onChange={(e) => handleFilterChange("city", e.target.value)}
                  placeholder="Enter city"
                />
              </div>

              {/* STATE */}
              <div className="ul-field">
                <label>State</label>
                <input
                  type="text"
                  value={filters.state}
                  onChange={(e) => handleFilterChange("state", e.target.value)}
                  placeholder="Enter state"
                />
              </div>

              {/* PINCODE */}
              <div className="ul-field">
                <label>Pincode</label>
                <input
                  type="text"
                  value={filters.pincode}
                  onChange={(e) => handleFilterChange("pincode", e.target.value)}
                  placeholder="Enter pincode"
                />
              </div>

              {/* PARENT CATEGORY */}
              <div className="ul-field">
                <label>Parent Category</label>
                <select
                  value={filters.parent_category_id}
                  onChange={(e) => handleFilterChange("parent_category_id", e.target.value)}
                >
                  <option value="">All</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* CHILD CATEGORY */}
              <div className="ul-field">
                <label>Child Category</label>
                <select
                  value={filters.child_category_id}
                  onChange={(e) => handleFilterChange("child_category_id", e.target.value)}
                >
                  <option value="">All</option>
                  {childCategories
                    .filter(c => c.parent_category_id?.toString() === filters.parent_category_id?.toString())
                    .map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                </select>
              </div>

              {/* SUBCHILD CATEGORY */}
              <div className="ul-field">
                <label>Subchild Category</label>
                <select
                  value={filters.subchild_category_id}
                  onChange={(e) => handleFilterChange("subchild_category_id", e.target.value)}
                >
                  <option value="">All</option>
                  {subchildCategories
                    .filter(c => c.child_category_id?.toString() === filters.child_category_id?.toString())
                    .map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                </select>
              </div>

              {/* STATUS */}
              <div className="ul-field">
                <label>Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  <option value="">All</option>
                  <option value="approved">Approved</option>
                  <option value="0">Inactive</option>
                </select>
              </div>

            </div>

            <div className="ul-filter-actions">
              <button className="ul-btn ul-btn-ghost" onClick={handleResetFilters}>
                Reset
              </button>
              <button className="ul-btn ul-btn-primary" onClick={handleApplyFilters}>
                Apply Filters
              </button>
            </div>
          </div>
        </div>

        {/* ====================== USER TABLE ====================== */}
        {loading ? (
          <div className="ul-results-card ul-state">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : error ? (
          <div className="ul-results-card ul-state">
            <div className="alert alert-danger d-inline-block">{error}</div>
            <div>
              <button onClick={() => window.location.reload()} className="ul-btn ul-btn-primary mt-2">
                Try Again
              </button>
            </div>
          </div>
        ) : filteredUsers.length > 0 ? (
          <div className="ul-results-card">
            <div className="ul-results-header">
              <h3>Users List</h3>
              <span>{filteredUsers.length} users found</span>
            </div>

            <div className="ul-table-wrap">
              <table className="ul-table">
                <thead>
                  <tr>
                    <th style={{ width: '76px' }}>Photo</th>
                    <th>
                      <button className="ul-th-btn" onClick={() => handleSort('name')}>
                        Name <SortIcon columnKey="name" />
                      </button>
                    </th>
                    <th>
                      <button className="ul-th-btn" onClick={() => handleSort('user_type')}>
                        User Type <SortIcon columnKey="user_type" />
                      </button>
                    </th>
                    <th>
                      <button className="ul-th-btn" onClick={() => handleSort('contact_no')}>
                        Mobile <SortIcon columnKey="contact_no" />
                      </button>
                    </th>
                    <th>City</th>
                    <th>State</th>
                    <th>Pincode</th>
                    <th>Category</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.slice(0, displayCount).map((user) => (
                    <tr key={user.user_id} onClick={() => handleUserClick(user)}>
                      <td>
                        <img
                          src={
                            user.profile_photo
                              ? `https://www.businessmateworkflow.in/uploads/profile-photos/${user.profile_photo}`
                              : DEFAULT_IMAGE
                          }
                          alt={user.name}
                          className="ul-avatar"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = DEFAULT_IMAGE;
                          }}
                        />
                      </td>
                      <td>
                        <span className="ul-name">{user.name}</span>
                        {user.status === '0' && (
                          <span className="ul-badge-inactive">Inactive</span>
                        )}
                      </td>
                      <td>
                        <span className="ul-type-chip" title={getUserTypeLabel(user.user_type)}>
                          {getUserTypeLabel(user.user_type)}
                        </span>
                      </td>
                      <td>
                        {renderProtectedValue(
                          user.contact_no,
                          user.user_type,
                          {
                            link: `tel:${user.contact_no}`,
                            className: 'ul-contact-link',
                            icon: <i className="las la-phone"></i>,
                          }
                        )}
                      </td>
                      <td>
                        <span className="ul-place">{user.city || <span className="ul-muted">N/A</span>}</span>
                      </td>
                      <td>{user.state || <span className="ul-muted">N/A</span>}</td>
                      <td>{user.pincode || <span className="ul-muted">N/A</span>}</td>
                      <td>
                        <span className="ul-place">{user.parent_category_name || <span className="ul-muted">N/A</span>}</span>
                      </td>
                      <td>
                        <button
                          className="ul-view-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUserClick(user);
                          }}
                        >
                          <i className="las la-eye"></i>
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {displayCount < filteredUsers.length && (
              <div className="ul-load-more">Loading more users...</div>
            )}
          </div>
        ) : (
          <div className="ul-results-card ul-state">
            <i className="las la-users ul-state-icon d-block"></i>
            <div>No users match your filter criteria.</div>
          </div>
        )}

      </section>

      <Footer />
    </div>
  );
};

export default UserList;